/**
 * Notification & FCM operations.
 *
 * Firestore collections:
 *   - user_fcm_tokens/{docId}    — FCM tokens per user/device
 *   - user_notification_prefs/{userId} — notification subscription preferences
 *
 * Works alongside the existing community_notifications collection
 * for in-app notifications.
 */
import { db, messagingPromise } from "../firebase";
import { getToken, onMessage } from "firebase/messaging";
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

// ── VAPID key for push subscriptions ──
// Generate via Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
// Store in env var REACT_APP_VAPID_KEY
const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || "";

// ══════════════════════════════════════════════════════
//  FCM Token Management
// ══════════════════════════════════════════════════════

/**
 * Request notification permission and register FCM token.
 * Saves token to Firestore keyed by userId + device fingerprint.
 * Returns the token string or null if declined/unsupported.
 */
export const requestNotificationPermission = async (userId) => {
  try {
    const messaging = await messagingPromise;
    if (!messaging) return null;

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const swReg = await navigator.serviceWorker.getRegistration();
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (token) {
      await saveToken(userId, token);
    }
    return token;
  } catch (err) {
    console.error("FCM token request failed:", err);
    return null;
  }
};

/**
 * Save FCM token to Firestore.
 */
const saveToken = async (userId, token) => {
  const docRef = doc(db, "user_fcm_tokens", `${userId}_${hashToken(token)}`);
  await setDoc(docRef, {
    userId,
    token,
    createdAt: serverTimestamp(),
    userAgent: navigator.userAgent,
  });
};

/**
 * Remove FCM token (on logout or manual unsubscribe).
 */
export const removeToken = async (userId, token) => {
  try {
    const docRef = doc(db, "user_fcm_tokens", `${userId}_${hashToken(token)}`);
    await deleteDoc(docRef);
  } catch (err) {
    console.error("Failed to remove FCM token:", err);
  }
};

/**
 * Get all FCM tokens for a user (for sending from Cloud Functions).
 */
export const getUserTokens = async (userId) => {
  const q = query(
    collection(db, "user_fcm_tokens"),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data().token);
};

/**
 * Simple hash for token dedup in doc IDs.
 */
function hashToken(token) {
  let h = 0;
  for (let i = 0; i < token.length; i++) {
    h = ((h << 5) - h + token.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

// ══════════════════════════════════════════════════════
//  Foreground Message Listener
// ══════════════════════════════════════════════════════

/**
 * Listen for foreground push messages.
 * Returns an unsubscribe function.
 */
export const onForegroundMessage = async (callback) => {
  const messaging = await messagingPromise;
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    callback(payload);
  });
};

// ══════════════════════════════════════════════════════
//  Notification Preferences
// ══════════════════════════════════════════════════════

/**
 * All subscribable notification categories.
 * Each module maps to notification types it can produce.
 */
export const NOTIFICATION_CATEGORIES = [
  {
    id: "visitors",
    label: "Visitors & Security",
    description: "Visitor check-ins, gate approvals, security alerts",
    icon: "PersonPin",
    defaultOn: true,
    priority: "high",
  },
  {
    id: "meetings",
    label: "Meetings & Invitations",
    description: "New meetings, reminders, status updates",
    icon: "CalendarMonth",
    defaultOn: true,
    priority: "medium",
  },
  {
    id: "finance",
    label: "Finance & Dues",
    description: "Payment reminders, new dues, account updates",
    icon: "CurrencyRupee",
    defaultOn: true,
    priority: "high",
  },
  {
    id: "maintenance",
    label: "Maintenance Requests",
    description: "New requests, status changes, assignments",
    icon: "Build",
    defaultOn: true,
    priority: "medium",
  },
  {
    id: "notices",
    label: "Notices & Announcements",
    description: "Community notices, important announcements",
    icon: "Campaign",
    defaultOn: true,
    priority: "high",
  },
  {
    id: "polls",
    label: "Polls & Voting",
    description: "New polls, voting reminders, results",
    icon: "HowToVote",
    defaultOn: true,
    priority: "medium",
  },
  {
    id: "chat",
    label: "Chat Messages",
    description: "New messages in community chats",
    icon: "Forum",
    defaultOn: false,
    priority: "low",
  },
  {
    id: "social",
    label: "Social Hub",
    description: "New posts, comments, reactions",
    icon: "DynamicFeed",
    defaultOn: false,
    priority: "low",
  },
  {
    id: "amenities",
    label: "Amenity Bookings",
    description: "Booking confirmations, reminders, cancellations",
    icon: "Event",
    defaultOn: true,
    priority: "medium",
  },
  {
    id: "community",
    label: "Community Updates",
    description: "New members, role changes, committee updates",
    icon: "Apartment",
    defaultOn: true,
    priority: "medium",
  },
];

/**
 * Get user's notification preferences.
 * Returns an object like { visitors: true, chat: false, ... }
 * Defaults to category.defaultOn if no prefs saved.
 */
export const getNotificationPrefs = async (userId) => {
  const docRef = doc(db, "user_notification_prefs", userId);
  const snap = await getDoc(docRef);
  if (snap.exists()) return snap.data();

  // Return defaults
  const defaults = {};
  NOTIFICATION_CATEGORIES.forEach((cat) => {
    defaults[cat.id] = cat.defaultOn;
  });
  return defaults;
};

/**
 * Save user's notification preferences.
 */
export const saveNotificationPrefs = async (userId, prefs) => {
  const docRef = doc(db, "user_notification_prefs", userId);
  await setDoc(docRef, { ...prefs, updatedAt: serverTimestamp() }, { merge: true });
};
